import MortgageDetails from "@/components/ReviewApplication/MortgageDetails";
import { ApplicationDto, InitIdVerificationDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import { generateSessionId, setClickInsightEvents } from "@/lib/util";
import { setAlloySessionId } from "@/slices/applicantSlice";
import { setDownPayment, setTermLength, setAmortizationLength, setApplicationProgress, setApplicationCategory, setTotalLoan, setApplicationStatus, setApplicationId, setJointCode, setJointPrimaryApplicant, setJointRelationship } from "@/slices/applicationSlice";
import { clearAssetState, assetInfo, addAsset } from "@/slices/assetSlice";
import { clearCoApplicantState, selectCoApplicant } from "@/slices/coApplicantSlice";
import { setCredentialsComplete } from "@/slices/progressSlice";
import { setPAStreetNumber, setPAStreetName, setPAUnitNumber, setPACity, setPAProvince, setPAPostalCode, setPACountry, setPAStreetType, setPAStreetDirection } from "@/slices/propertyAddressSlice";
import { clearPropertyInfoState, selectPropertyInfo, setClosingDate, setFinancingConditionDate, setFinancingConditionExists, setFundingDate, setHeatingCost, setHeatingType, setLivableArea, setLivableAreaUnit, setMaintenanceFee, setMonthlyRent, setMortgageBalance, setMortgageHolder, setNumOfRentalProperties, setNumOfRentalUnits, setOriginalPurchasePrice, setOwnershipPercentage, setOwnOtherRentalProperties, setPropertyTax, setPropertyValue, setPurchasePrice, setPurposeOfProperty, setRentOut, setSeparateUnit, setSubjectPropertyRentalUnits } from "@/slices/propertyInfoSlice";
import { setCachedAssets } from "@/slices/sessionDataSlice";
import { RootState } from "@/store/dtm";
import SlideTransition from "@/styles/animation/SlideTransition";
import style from "@/styles/pages/ReviewApplication/ReviewApplication.module.css";
import { applicationCategory } from "@/types/applicationType";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function JointMortgageDetails() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const [fetchingDetails, setFetchingDetails] = useState(true);

    function handleClick() {
        dispatch(setApplicationProgress(7));
        navigate("/joint/personal-info");
    }

    useEffect(() => {
        document.title = "Review mortgage details";

        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
        dispatch(setCredentialsComplete(true));

        client.apiDealsGet().then(deal => {
            if (deal != undefined) {
                dispatch(setApplicationStatus(deal.status ? deal.status : ""));

                if (deal.status && deal.status == "Cancelled") {
                    flushSync(() => {
                        dispatch(setApplicationProgress(0));
                        dispatch(setJointCode(""));
                        dispatch(setJointRelationship(""));
                        dispatch(setJointPrimaryApplicant(""));
                        dispatch(clearPropertyInfoState())
                        dispatch(clearCoApplicantState());

                        //Navigate back to home
                        navigate("/home", { replace: true });
                    });
                } else {
                    initializeIDVerification();
                    fetchMortgageDetails();
                }
            }
        }).catch((error) => {
            console.log(error);
            navigate("/error/3");
        });
    }, []);

    const initializeIDVerification = () => {
        if (applicant.alloySessionId == "") {
            let newAlloySessionId = generateSessionId(application.isCoApplicant);
            client.apiIdverificationInit(new InitIdVerificationDto({
                alloySessionId: newAlloySessionId,
                applicationId: application.id,
                applicantId: applicant.id,
                status: "PENDING",
                phoneVerified: true
            })).then(function (data) {

            }).catch((error) => {
                console.log(error);
            });
            dispatch(setAlloySessionId(newAlloySessionId));
        }
    }

    const fetchMortgageDetails = () => {
        //Fetch Mortgage Details and display them here
        client.apiDealsApplicationsMortgages(application.jointCode, undefined).then(application => {
            if (application && application.dealId != undefined) {
                dispatch(setDownPayment(application.downPaymentAmount ? application.downPaymentAmount : 0));
                if (application.products && application.products.length > 0) {
                    dispatch(setTermLength(application.products[0].termInMonths ? application.products[0].termInMonths / 12 : "unknown"));
                    dispatch(setAmortizationLength(application.products[0].amortizationInMonths ? application.products[0].amortizationInMonths / 12 : "unknown"));
                }
                switch (application.purposeType) {
                    case "Purchase":
                        dispatch(setApplicationCategory(applicationCategory.Buy));
                        break;
                    case "Refinance":
                        dispatch(setApplicationCategory(applicationCategory.Refinance));
                        break;
                    case "Transfer":
                        dispatch(setApplicationCategory(applicationCategory.Transfer));
                        break;
                }
                if (application.purposeType !== "Purchase") {
                    //Only applies to refinance and transfer
                    fetchAssets();
                }

                dispatch(setMortgageBalance(application.remainingBalanceAmount ? application.remainingBalanceAmount : 0));
                dispatch(setTotalLoan(application.totalLoanAmount ? application.totalLoanAmount : 0));
                if (application.subjectProperty) {
                    //Fetch other property details (i.e. heating cost, property tax, etc.)
                    dispatch(setPurposeOfProperty(application.subjectProperty.occupancyType ? application.subjectProperty.occupancyType : ""));
                    dispatch(setPurchasePrice(application.subjectProperty.purchasePrice ? application.subjectProperty.purchasePrice : 0));
                    dispatch(setOriginalPurchasePrice(application.subjectProperty.originalPurchasePrice ? application.subjectProperty.originalPurchasePrice : 0));
                    dispatch(setPropertyValue(application.subjectProperty.purchasePrice ? application.subjectProperty.purchasePrice : 0));
                    dispatch(setMortgageHolder(application.subjectProperty.mortgageHolder ? application.subjectProperty.mortgageHolder : ""));
                    dispatch(setPropertyTax(application.subjectProperty.propertyTax ? application.subjectProperty.propertyTax : 0));
                    dispatch(setLivableArea(application.subjectProperty.livableArea ? application.subjectProperty.livableArea : 0));
                    dispatch(setLivableAreaUnit(application.subjectProperty.livableAreaType ? application.subjectProperty.livableAreaType : ""));
                    dispatch(setHeatingCost(application.subjectProperty.heatingCost ? application.subjectProperty.heatingCost : 0));
                    dispatch(setHeatingType(application.subjectProperty.heatingType ? application.subjectProperty.heatingType : ""));
                    dispatch(setMaintenanceFee(application.subjectProperty.condoFees ? application.subjectProperty.condoFees : 0));
                    dispatch(setRentOut(application.subjectProperty.isPlanningOnRenting ? application.subjectProperty.isPlanningOnRenting : false));
                    dispatch(setSeparateUnit(application.subjectProperty.isSeparateLegalUnit ? application.subjectProperty.isSeparateLegalUnit : false));
                    dispatch(setMonthlyRent(application.subjectProperty.rent ? application.subjectProperty.rent : 0));
                    dispatch(setOwnOtherRentalProperties(application.subjectProperty.hasCurrentRentalProperties ? application.subjectProperty.hasCurrentRentalProperties : false));
                    dispatch(setSubjectPropertyRentalUnits(application.subjectProperty.subjectPropertyRentalUnits ? application.subjectProperty.subjectPropertyRentalUnits : 0));
                    dispatch(setNumOfRentalProperties(application.subjectProperty.totalRentalProperties ? application.subjectProperty.totalRentalProperties : 0));
                    dispatch(setNumOfRentalUnits(application.subjectProperty.totalRentalUnits ? application.subjectProperty.totalRentalUnits : 0));
                    dispatch(setOwnershipPercentage(application.subjectProperty.ownershipPercentage ? application.subjectProperty.ownershipPercentage : undefined));

                    if (application.subjectProperty.closingDate) {
                        let closingDateJs = application.subjectProperty.closingDate;
                        let closingMonth = (closingDateJs.month() + 1).toString();
                        let dateString =
                            `${closingDateJs.year()}-${closingMonth.length > 1 ? closingMonth : "0" + closingMonth}-` +
                            `${closingDateJs.date().toString().length > 1 ? closingDateJs.date() : "0" + closingDateJs.date()}`
                        dispatch(setClosingDate(dateString));
                    }
                    if (application.subjectProperty.estimatedFundingDate) {
                        let fundingDateJs = application.subjectProperty.estimatedFundingDate;
                        let fundingMonth = (fundingDateJs.month() + 1).toString();
                        let dateString =
                            `${fundingDateJs.year()}-${fundingMonth.length > 1 ? fundingMonth : "0" + fundingMonth}-` +
                            `${fundingDateJs.date().toString().length > 1 ? fundingDateJs.date() : "0" + fundingDateJs.date()}`
                        dispatch(setFundingDate(dateString));
                    }
                    if (application.subjectProperty.conditionOfFinancingDate) {
                        let conditionDateJs = application.subjectProperty.conditionOfFinancingDate;
                        let conditionMonth = (conditionDateJs.month() + 1).toString();
                        let dateString =
                            `${conditionDateJs.year()}-${conditionMonth.length > 1 ? conditionMonth : "0" + conditionMonth}-` +
                            `${conditionDateJs.date().toString().length > 1 ? conditionDateJs.date() : "0" + conditionDateJs.date()}`
                        dispatch(setFinancingConditionExists("yes"));
                        dispatch(setFinancingConditionDate(dateString));
                    }

                    if (application.subjectProperty.address && application.subjectProperty.address.streetNumber != null) {
                        dispatch(setPAStreetNumber(application.subjectProperty.address.streetNumber));
                        dispatch(setPAStreetName(application.subjectProperty.address.streetName ? application.subjectProperty.address.streetName : ""));
                        dispatch(setPAStreetType(application.subjectProperty.address.streetType ? application.subjectProperty.address.streetType : ""));
                        dispatch(setPAStreetDirection(application.subjectProperty.address.streetDirection ? application.subjectProperty.address.streetDirection : ""));
                        dispatch(setPAUnitNumber(application.subjectProperty.address.unitNumber ? application.subjectProperty.address.unitNumber : ""));
                        dispatch(setPACity(application.subjectProperty.address.city));
                        dispatch(setPAProvince(application.subjectProperty.address.region ? application.subjectProperty.address.region : ""));
                        dispatch(setPAPostalCode(application.subjectProperty.address.postalCode ? application.subjectProperty.address.postalCode : ""));
                        dispatch(setPACountry(application.subjectProperty.address.country));
                    }
                }
            }
            setFetchingDetails(false);
        }).catch((error) => {
            console.log("Failed to fetch mortgage details!")
            setFetchingDetails(false);
        });
    }

    const fetchAssets = () => {
        //Fetch Assets
        client.apiAssetsGet().then(assets => {
            dispatch(clearAssetState());
            let displayId = 1;
            let cachedAssets: assetInfo[] = [];
            if (assets && assets.nonRegistered) {
                assets.nonRegistered.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: {
                                financialInstitution: asset.fiName ? asset.fiName : "",
                                otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                            },
                            registered: undefined,
                            realEstate: undefined,
                            vehicle: undefined,
                            lifeInsurance: undefined
                        }
                    }
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            if (assets && assets.registered) {
                assets.registered.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: {
                                financialInstitution: asset.fiName ? asset.fiName : "",
                                otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                            },
                            realEstate: undefined,
                            vehicle: undefined,
                            lifeInsurance: undefined
                        }
                    }
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            if (assets && assets.realEstate) {
                assets.realEstate.forEach(asset => {
                    let originalPurchaseDateString = "";
                    let soldDate = "";
                    if (asset.origPurchasePrice) {
                        originalPurchaseDateString = asset.origPurchaseDate ? asset.origPurchaseDate.format("DD/MM/YYYY") : ""
                    }
                    if (asset.soldDate) {
                        soldDate = asset.soldDate ? asset.soldDate.format("DD/MM/YYYY") : ""
                    }
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: undefined,
                            realEstate: {
                                occupancyType: asset.occupancyType_Name ? asset.occupancyType_Name : "",
                                propertyType: asset.propertyType_Name ? asset.propertyType_Name : "",
                                rentalComponent: asset.rentalComponent ? "yes" : "no",
                                numOfProperties: asset.numberOfProperties ? asset.numberOfProperties.toString() : "",
                                address: {
                                    displayId: "1",
                                    streetNumber: asset.assetAddress_StreetNumber ? asset.assetAddress_StreetNumber : "",
                                    streetName: asset.assetAddress_StreetName ? asset.assetAddress_StreetName : "",
                                    streetType: asset.assetAddress_StreetType ? asset.assetAddress_StreetType : "",
                                    streetDirection: asset.assetAddress_StreetDirection ? asset.assetAddress_StreetDirection : "",
                                    unitType: asset.assetAddress_UnitType ? asset.assetAddress_UnitType : "",
                                    unitNumber: asset.assetAddress_UnitNumber ? asset.assetAddress_UnitNumber : "",
                                    city: asset.assetAddress_City ? asset.assetAddress_City : "",
                                    province: asset.assetAddress_Region ? asset.assetAddress_Region : "",
                                    postalCode: asset.assetAddress_PostalCode ? asset.assetAddress_PostalCode : "",
                                    country: asset.assetAddress_Country ? asset.assetAddress_Country : "",
                                },
                                originalPurchasePrice: asset.origPurchasePrice ? asset.origPurchasePrice : 0,
                                originalPurchasePriceText: asset.origPurchasePrice ? asset.origPurchasePrice.toString() : "",
                                originalPurchaseDate: originalPurchaseDateString,
                                propertyTax: 0,
                                propertyTaxText: "",
                                heatingCost: 0,
                                heatingCostText: "",
                                maintenanceFee: 0,
                                maintenanceFeeText: "",
                                ownershipPercentage: "",
                                lot: asset.lot ? asset.lot : "",
                                block: asset.block ? asset.block : "",
                                plan: asset.plan ? asset.plan : "",
                                section: asset.section ? asset.section : "",
                                futureUsage: asset.futureUsageType_Name ? asset.futureUsageType_Name : "",
                                hasPropertyBeenSold: asset.hasPropertyBeenSold ? "yes" : "no",
                                soldDate: soldDate,
                                rentalIncome: asset.rentalIncome ? asset.rentalIncome : 0,
                                rentalIncomeText: asset.rentalIncome ? asset.rentalIncome.toString() : "",
                                manualAddressEntered: false,
                                isCurrentResidence: false
                            },
                            vehicle: undefined,
                            lifeInsurance: undefined
                        }
                    }
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            if (assets && assets.vehicle) {
                assets.vehicle.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: undefined,
                            realEstate: undefined,
                            vehicle: {
                                make: asset.make ? asset.make : "",
                                model: asset.model ? asset.model : "",
                                year: asset.year ? asset.year.toString() : "",
                                ownership: asset.vehicleOwnershipType_Name ? asset.vehicleOwnershipType_Name : "",
                            },
                            lifeInsurance: undefined
                        }
                    };
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            if (assets && assets.lifeInsurance) {
                assets.lifeInsurance.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: undefined,
                            realEstate: undefined,
                            vehicle: undefined,
                            lifeInsurance: {
                                cashSurrenderValue: asset.cashSurrenderValue ? asset.cashSurrenderValue : 0,
                                cashSurrenderValueText: asset.cashSurrenderValue ? asset.cashSurrenderValue.toString() : "",
                            }
                        }
                    };
                    dispatch(addAsset(newAsset));
                    displayId++;
                });
            }
            if (assets && assets.other) {
                assets.other.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.value ? asset.value : 0,
                        valueText: asset.value != undefined ? asset.value.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: undefined,
                            realEstate: undefined,
                            vehicle: undefined,
                            lifeInsurance: undefined
                        }
                    };
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            if (assets && assets.downPayment) {
                assets.downPayment.forEach(asset => {
                    let newAsset: assetInfo = {
                        displayId: displayId.toString(),
                        id: asset.id ? asset.id : 0,
                        visible: false,
                        assetCategory: asset.assetType_Name ? asset.assetType_Name : "",
                        assetType: asset.assetType_Name ? asset.assetType_Name : "",
                        origin: asset.origin_Name ? asset.origin_Name : "",
                        value: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        valueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        description: asset.description ? asset.description : "",
                        downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                        downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                        applicants: asset.applicants ? asset.applicants : [],
                        details: {
                            nonRegistered: undefined,
                            registered: undefined,
                            realEstate: undefined,
                            vehicle: undefined,
                            lifeInsurance: undefined
                        }
                    };
                    dispatch(addAsset(newAsset));
                    cachedAssets.push(newAsset);
                    displayId++;
                });
            }
            dispatch(setCachedAssets(cachedAssets));
        });
    }

    return (
        <SlideTransition>
            <main aria-labelledby="main-content">
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div className={style.raHeading}>
                        <h1>Your mortgage details</h1>
                        <p className="sub-heading">
                            These are the details {application.jointPrimaryApplicant != "" ? application.jointPrimaryApplicant : "your primary applicant"} has provided for your mortgage.
                        </p>
                        <div className={style.sectionContainer}>
                            <hr className="mt-5" />
                            {fetchingDetails &&
                                <div className="justify-center flex mt-5">
                                    <FontAwesomeIcon icon={faSpinner} spin style={{ margin: 'auto' }} className={clsx("")} />
                                </div>
                            }
                            {!fetchingDetails && <MortgageDetails editModeOn={false} restrictEditing={true} />}
                            <hr className="mt-5" />
                        </div>
                    </div>
                    <div className="flex max-sm:hidden">
                        <div className="mb-5">
                            <button
                                type="submit"
                                onClick={handleClick}
                                disabled={application.status == "Cancelled"}
                                className={clsx("mt-5 w-full sm:w-28 h-11 px-6", 
                                    application.status == "Cancelled" ? style.editMode : "primary")}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                    <div className="sm:hidden">
                        <div className="mb-5">
                            <button
                                type="submit"
                                onClick={handleClick}
                                disabled={application.status == "Cancelled"}
                                className={clsx("mt-5 w-full sm:w-28 h-11 px-6", 
                                    application.status == "Cancelled" ? style.editMode : "primary")}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </SlideTransition>
    );
}
